// frontend/src/components/FinalResult.js

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getFinalOnlyResults, updateUserFlag } from '../services/api';
import { Button } from "@/components/ui/button";
import LoadingSpinner from './LoadingSpinner';
import {
  CheckCircle2,
  AlertCircle,
  Building2,
  ExternalLink
} from 'lucide-react';
import '../styles/Dashboard.css';
import '../styles/FinalResult.css';

const FinalResult = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [results, setResults] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!user) {
      navigate('/login');
    } else {
      fetchFinalResults();
    }
  }, [user, navigate]);

  // 新しい API 関数を利用して最終結果のみを取得
  const fetchFinalResults = async () => {
    try {
      const finalResults = await getFinalOnlyResults();
      setResults(finalResults);
    } catch (err) {
      console.error('結果の取得に失敗しました:', err);
      setError('結果の取得に失敗しました。');
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirmClick = async () => {
    alert('結果を確定しました。');
    await updateUserFlag(user.uid, 'final_result_status', true);
    navigate('/dashboard');
  };

  if (!user) return null;
  if (isLoading) {
    return <LoadingSpinner text="最終結果を読み込んでいます..." />;
  }
  if (error) {
    return (
      <div className="flex justify-center items-center h-screen bg-background">
        <div className="text-accent text-xl">{error}</div>
      </div>
    );
  }

  return (
    <div className="dashboard final-result final-result-scope">
      <div className="dashboard-container">
        {/* ヘッダーセクション */}
        <div className="final-result-header">
          <h1 className="dashboard-title final-result-title flex items-center gap-3 font-bold">
            最終配属結果
          </h1>
          <p className="text-gray-600 mt-2 final-result-subtitle">
            {user.displayName || user.email}さんの最終的な配属先一覧です。
            内容を確認し、問題がなければ「確認しました」ボタンを押してください。
          </p>
        </div>

        {/* 結果カード */}
        <div className="dashboard-card final-result-card">
          <div className="card-header final-result-card-header">
            <h2 className="card-title flex items-center gap-2">
              <Building2 className="text-blue-600" />
              配属一覧
            </h2>
          </div>

          <div className="final-result-grid">
            {results.map((result, index) => (
              <div key={index} className="final-result-item">
                <div className="final-result-item-content">

                  {/* Column 1: Term */}
                  <div className="final-result-col term-col">
                    <div className="final-result-term-badge">
                      {result.term_id}
                    </div>
                    <span className="final-result-term-label">第{result.term_id}クール</span>
                  </div>

                  {/* Column 2: Institution */}
                  <div className="final-result-col institution-col">
                    <span className="final-result-label">配属先</span>
                    <div className="final-result-institution">
                      {result.institution_name}
                    </div>
                  </div>

                  {/* Column 3: Accommodation */}
                  <div className="final-result-col accommodation-col">
                    <span className="final-result-label">宿泊</span>
                    <div className={`final-result-accommodation ${result.accommodation ? 'status-active' : 'status-inactive'}`}>
                      {result.accommodation ? '宿泊する' : '宿泊しない'}
                    </div>
                  </div>

                </div>
              </div>
            ))}
          </div>
        </div>

        {/* アクションボタンエリア */}
        <div className="final-result-actions">
          <Button
            onClick={handleConfirmClick}
            className="final-result-confirm-btn"
          >
            <CheckCircle2 />
            結果を確認しました
          </Button>

          <a
            href="https://20m-hirosaki-u.notion.site/1368f5933a2d80609dfac7dc11a67973?pvs=105"
            target="_blank"
            rel="noopener noreferrer"
            className="final-result-link-wrapper"
          >
            <Button variant="outline" className="final-result-outline-btn">
              <AlertCircle className="text-orange-500" />
              問題を報告する
              <ExternalLink size={16} />
            </Button>
          </a>
        </div>
      </div>
    </div>
  );
};

export default FinalResult;
